const API_BASE = '';

// Generate human-friendly ticket reference from ticketNumber
// Format: TK-X (e.g., TK-1, TK-42, TK-1234)
export function formatTicketRef(ticketNumber?: number | null): string {
  if (ticketNumber != null) {
    return `TK-${ticketNumber}`;
  }
  // Fallback for tickets without number
  return 'TK-?';
}

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers
    },
    credentials: 'include' // Important for cookies/auth
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText })) as { message?: string };
    throw new Error(error.message || `API Error: ${response.status}`);
  }

  return response.json();
}

export const api = {
  // Auth
  getMe: () => fetchApi<{ user: User | null; modules?: Modules; _timestamp: number }>('/api/auth/me'),
  login: (data: { email: string; password: string }) =>
    fetchApi<{ success: boolean; user: User }>('/api/auth/login', { method: 'POST', body: JSON.stringify(data) }),
  logout: () => fetchApi<{ success: boolean }>('/api/auth/logout', { method: 'POST' }),
  
  // Dashboard
  getDashboard: () => fetchApi<DashboardData>('/api/dashboard'),
  
  // QR Code Scanner
  scanQrCode: (code: string) => fetchApi<ScanResult>(`/api/scan/${encodeURIComponent(code)}`),
  
  // Pending Actions
  getPendingActions: () => fetchApi<PendingActionsResponse>('/api/pending-actions'),
  
  // Tickets
  getTickets: (params?: URLSearchParams) => 
    fetchApi<TicketsResponse>(`/api/tickets${params ? `?${params}` : ''}`),
  getTicket: (id: string) => fetchApi<TicketDetailResponse>(`/api/tickets/${id}`),
  createTicket: (data: CreateTicketData) => 
    fetchApi<{ ticket: { id: string }; success: boolean }>('/api/tickets', { method: 'POST', body: JSON.stringify(data) }),
  updateTicket: (id: string, data: UpdateTicketData) => 
    fetchApi<{ success: boolean }>(`/api/tickets/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  createTicketEvent: (ticketId: string, data: CreateEventData) =>
    fetchApi<{ success: boolean; eventId: string }>(`/api/tickets/${ticketId}/events`, { method: 'POST', body: JSON.stringify(data) }),
  updateTicketEvent: (ticketId: string, data: UpdateEventData) =>
    fetchApi<{ success: boolean }>(`/api/tickets/${ticketId}/events`, { method: 'PATCH', body: JSON.stringify(data) }),
  
  // Assets
  getAssets: (params?: URLSearchParams) => 
    fetchApi<AssetsResponse>(`/api/assets${params ? `?${params}` : ''}`),
  getAsset: (id: string) => fetchApi<AssetDetailResponse>(`/api/assets/${id}`),
  createAsset: (data: CreateAssetData) => 
    fetchApi<{ asset: { id: string }; success: boolean }>('/api/assets', { method: 'POST', body: JSON.stringify(data) }),
  updateAsset: (id: string, data: UpdateAssetData) => 
    fetchApi<{ success: boolean }>(`/api/assets/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteAsset: (id: string) => 
    fetchApi<{ success: boolean }>(`/api/assets/${id}`, { method: 'DELETE' }),
  
  // Locations
  getLocations: () => fetchApi<LocationsResponse>('/api/locations'),
  getLocation: (id: string) => fetchApi<LocationDetailResponse>(`/api/locations/${id}`),
  createLocation: (data: CreateLocationData) => 
    fetchApi<{ id: string; success: boolean }>('/api/locations', { method: 'POST', body: JSON.stringify(data) }),
  updateLocation: (id: string, data: Partial<CreateLocationData>) => 
    fetchApi<{ success: boolean }>(`/api/locations/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteLocation: (id: string) => 
    fetchApi<{ success: boolean }>(`/api/locations/${id}`, { method: 'DELETE' }),
  moveAssetsToLocation: (locationId: string, assetIds: string[], completed: boolean) =>
    fetchApi<{ success: boolean; movedCount: number; movedAssets: string[]; newStatus: string }>(`/api/locations/${locationId}/move-assets`, {
      method: 'POST',
      body: JSON.stringify({ assetIds, completed })
    }),
  
  // Categories
  getCategories: () => fetchApi<CategoriesResponse>('/api/categories'),
  getCategory: (id: string) => fetchApi<CategoryDetailResponse>(`/api/categories/${id}`),
  createCategory: (data: CreateCategoryData) => 
    fetchApi<{ id: string; success: boolean }>('/api/categories', { method: 'POST', body: JSON.stringify(data) }),
  updateCategory: (id: string, data: Partial<CreateCategoryData>) => 
    fetchApi<{ success: boolean }>(`/api/categories/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteCategory: (id: string) => 
    fetchApi<{ success: boolean }>(`/api/categories/${id}`, { method: 'DELETE' }),
};

// Types
export interface Modules {
  ticketingEnabled: boolean;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  tenantId: string;
}

export interface DashboardData {
  ticketingEnabled: boolean;
  tickets: {
    open: number;
    inProgress: number;
    waiting: number;
    resolved: number;
    closed: number;
  };
  assets: {
    total: number;
    active: number;
    inRepair: number;
    inMaintenance: number;
    inStorage: number;
    inTransit: number;
    disposed: number;
    lost: number;
  };
  assetConditions: {
    excellent: number;
    good: number;
    fair: number;
    poor: number;
    broken: number;
  };
  locations: {
    total: number;
    byType: Record<string, number>;
  };
  categories: number;
  topLocations: Array<{
    id: string;
    name: string;
    type: string;
    assetCount: number;
  }>;
  recentTickets: Array<{
    id: string;
    ticketNumber?: number;
    title: string;
    status: string;
    priority: string;
    createdAt: number;
    asset: { id: string; name: string } | null;
    assignee: { id: string; firstName: string; lastName: string } | null;
    location: { id: string; name: string } | null;
  }>;
  recentEvents: Array<{
    id: string;
    eventType: string;
    description: string | null;
    createdAt: number;
    asset: { id: string; name: string } | null;
    user: { firstName: string; lastName: string } | null;
  }>;
  recentAssetEvents: Array<{
    id: string;
    action: string;
    description: string | null;
    createdAt: number;
    asset: { id: string; name: string } | null;
    user: { firstName: string; lastName: string } | null;
  }>;
  spendingStats?: {
    maintenance: {
      week: number;
      month: number;
      year: number;
      total: number;
    };
    purchases: {
      week: number;
      month: number;
      year: number;
      total: number;
    };
  };
  upcomingMaintenances?: UpcomingMaintenance[];
  _timestamp: number;
}

export interface Ticket {
  id: string;
  ticketNumber?: number;
  title: string;
  description: string | null;
  type: string;
  status: string;
  priority: string;
  assetId: string | null;
  locationId: string | null;
  assignedTo: string | null;
  createdBy: string;
  createdAt: string | number;
  updatedAt: string | number;
  resolvedAt: string | number | null;
  closedAt: string | number | null;
  asset?: Asset;
  location?: { id: string; name: string };
  assignedToUser?: User;
  createdByUser?: User;
}

export interface TicketEvent {
  id: string;
  ticketId: string;
  eventType: string;
  description: string | null;
  isCompleted: boolean;
  cost: number | null;
  laborMinutes: number | null;
  fromValue: string | null;
  toValue: string | null;
  toStatus: string | null;
  fromStatus: string | null;
  createdAt: string | number;
  user?: User;
  asset?: Asset;
  fromLocation?: Location;
  toLocation?: Location;
}

export interface Asset {
  id: string;
  qrCode: string;
  name: string;
  description: string | null;
  categoryId: string;
  locationId: string | null;
  status: string;
  condition: string;
  // Dettagli prodotto
  brand: string | null;
  model: string | null;
  serialNumber: string | null;
  // Acquisto
  purchaseDate: string | number | null;
  purchasePrice: number | null;
  purchaseVendor: string | null;
  invoiceNumber: string | null;
  // Garanzia
  warrantyExpiresAt: string | number | null;
  // Costi
  totalMaintenanceCost: number;
  // Prossima manutenzione programmata
  nextMaintenanceDate: number | null;
  // Azione incompleta
  hasIncompleteAction?: boolean;
  // Other
  notes: string | null;
  photoUrls: string | null;
  createdAt: string | number;
  updatedAt: string | number;
  location?: Location;
  category?: Category;
}

export interface Location {
  id: string;
  name: string;
  type: string;
  code?: string;
  description: string | null;
  address: string | null;
  parentId: string | null;
  capacity?: number;
  latitude?: string;
  longitude?: string;
  mapsLink?: string;
  photoUrl?: string;
  assetCount?: number;
  isActive?: boolean;
  createdAt?: number;
}

export interface Category {
  id: string;
  name: string;
  icon: string;
  color: string;
  description: string | null;
  assetCount?: number;
}

export interface TicketsResponse {
  tickets: Ticket[];
  stats: { open: number; inProgress: number; urgent: number; waiting: number };
  _timestamp: string;
}

export interface TicketDetailResponse {
  ticket: Ticket;
  events: TicketEvent[];
  technicians: User[];
  locations: Location[];
  availableAssets: Asset[];
  _timestamp: string;
}

export interface AssetsResponse {
  assets: Asset[];
  categories: Category[];
  locations: Location[];
  _timestamp: string;
}

export interface AssetDetailResponse {
  asset: Asset;
  history: any[];
  tickets: Ticket[];
  scheduledMaintenances: ScheduledMaintenance[];
  locations: Location[];
  categories: Category[];
  hasOpenTicket: boolean;
  openTicketId: string | null;
  hasIncompleteAction: boolean;
  incompleteActionId: string | null;
  _timestamp: string;
}

export interface ScheduledMaintenance {
  id: string;
  description: string;
  intervalMonths: number;
  estimatedCost: number | null;
  referenceType: 'asset_creation' | 'purchase_date' | 'custom';
  customReferenceDate: number | null;
  isActive: boolean;
  nextMaintenanceDate: number;
  createdAt: number;
  updatedAt: number;
}

export interface UpcomingMaintenance {
  id: string;
  assetId: string;
  assetName: string;
  assetQrCode: string;
  description: string;
  intervalMonths: number;
  estimatedCost: number | null;
  nextMaintenanceDate: number;
  isOverdue: boolean;
  location: { id: string; name: string } | null;
  category: { id: string; name: string } | null;
}

export interface LocationsResponse {
  locations: Location[];
  _timestamp: string;
}

export interface LocationDetailResponse {
  location: Location;
  assets: Asset[];
  children: Array<{ id: string; name: string; type: string }>;
  tickets: Array<{ id: string; ticketNumber?: number; title: string; status: string; priority: string; createdAt: number }>;
  ticketingEnabled: boolean;
  _timestamp: string;
}

export interface CategoriesResponse {
  categories: Category[];
  _timestamp: string;
}

export interface CategoryDetailResponse {
  category: Category;
  assets: Array<{
    id: string;
    name: string;
    qrCode: string;
    status: string;
    condition: string;
    location: { id: string; name: string } | null;
  }>;
  _timestamp: number;
}

export interface CreateTicketData {
  title: string;
  description?: string;
  assetId?: string;
  locationId?: string;
  priority?: string;
  type?: string;
}

export interface UpdateTicketData {
  action: 'updateStatus' | 'assign' | 'updatePriority' | 'close' | 'linkAsset' | 'linkLocation';
  status?: string;
  assignedTo?: string | null;
  priority?: string;
  finalCondition?: string;
  resolutionNotes?: string;
  assetId?: string;
  locationId?: string;
}

export interface CreateEventData {
  eventType: string;
  description?: string;
  toLocationId?: string;
  toStatus?: string;
  isCompleted?: boolean;
  cost?: number;
  laborMinutes?: number;
  replacementAssetId?: string;
  updateAssetLocation?: boolean;
}

export interface UpdateEventData {
  eventId: string;
  cost?: number | null;
}

export interface CreateAssetData {
  name: string;
  qrCode?: string;
  categoryId?: string;
  locationId?: string;
  status?: string;
  serialNumber?: string;
  manufacturer?: string;
  model?: string;
  purchaseDate?: string;
  purchasePrice?: number;
  condition?: string;
  notes?: string;
  photoUrls?: string[];
  scheduledMaintenances?: Array<{
    description: string;
    intervalMonths: number;
    estimatedCost?: number;
    referenceType: 'asset_creation' | 'purchase_date' | 'custom';
    customReferenceDate?: string;
  }>;
}

export interface UpdateAssetData {
  action: 'move' | 'updateStatus' | 'updateCondition' | 'update' | 'maintenance' | 'repair' | 'changeStatus' | 'completeIntervention';
  locationId?: string;
  status?: string;
  condition?: string;
  notes?: string;
  name?: string;
  categoryId?: string;
  description?: string;
  newCondition?: string;
  cost?: number;
  laborMinutes?: number;
  isCompleted?: boolean;
  historyId?: string;
  [key: string]: any;
}

export interface CreateLocationData {
  name: string;
  type?: string;
  description?: string;
  parentId?: string;
  code?: string;
  capacity?: number;
  latitude?: number;
  longitude?: number;
  mapsLink?: string;
  photoUrl?: string;
}

export interface CreateCategoryData {
  name: string;
  icon?: string;
  color?: string;
  description?: string;
}

export interface ScanResult {
  found: boolean;
  type?: 'asset' | 'location';
  id?: string;
  name?: string;
  qrCode?: string;
  code?: string;
  _timestamp: number;
}

export interface PendingAction {
  id: string;
  actionType: string;
  description: string | null;
  createdAt: number;
  startedBy: { id: string; firstName: string; lastName: string } | null;
  isOwnAction: boolean;
  asset: {
    id: string;
    name: string;
    qrCode: string;
    status: string;
    condition: string;
    currentLocation: { id: string; name: string } | null;
  };
  fromLocation: { id: string; name: string; qrCode?: string } | null;
  toLocation: { id: string; name: string; qrCode?: string } | null;
}

export interface PendingActionsResponse {
  pendingActions: PendingAction[];
  stats: {
    total: number;
    moves: number;
    maintenance: number;
    repairs: number;
    own: number;
  };
  _timestamp: number;
}
